(() => {
  'use strict';

  if (window.__mzBackInStockInitialized) return;
  window.__mzBackInStockInitialized = true;

  const KLAVIYO_SITE_ID = 'U6rwVN';
  const KLAVIYO_REVISION = '2026-04-15';
  const KLAVIYO_ENDPOINT =
    'https://a.klaviyo.com/client/back-in-stock-subscriptions' +
    '?company_id=' +
    encodeURIComponent(KLAVIYO_SITE_ID);

  let lastTrigger = null;
  let isSubmitting = false;
  let modalOpen = false;
  let uiGeneration = 0;
  let closeTimer = null;
  let openFrame = null;
  const inertBackground = new Map();
  let layoutTagged = false;
  let layoutObserver = null;

  const callClarity = (...args) => {
    try {
      window.clarity = window.clarity || function () {
        (window.clarity.q = window.clarity.q || []).push(arguments);
      };
      window.clarity(...args);
    } catch {}
  };

  const tagLayout = (root = document) => {
    if (
      layoutTagged ||
      (!root.matches?.('[data-mezuzah-bis-open]') &&
        !root.querySelector?.('[data-mezuzah-bis-open]'))
    ) {
      return;
    }
    layoutTagged = true;
    callClarity('set', 'bis_layout', 'single');
    layoutObserver?.disconnect();
  };

  const cancelScheduledTransition = () => {
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    if (openFrame !== null) {
      window.cancelAnimationFrame(openFrame);
      openFrame = null;
    }
  };

  const isolateBackground = (modal) => {
    Array.from(document.body.children).forEach((element) => {
      if (
        element === modal ||
        element.contains(modal) ||
        /^(SCRIPT|STYLE|LINK)$/.test(element.tagName) ||
        inertBackground.has(element)
      ) {
        return;
      }
      inertBackground.set(element, element.getAttribute('inert'));
      element.setAttribute('inert', '');
    });
  };

  const restoreBackground = () => {
    inertBackground.forEach((previous, element) => {
      if (previous === null) {
        element.removeAttribute('inert');
      } else {
        element.setAttribute('inert', previous);
      }
    });
    inertBackground.clear();
  };

  const getFocusableElements = (modal) =>
    Array.from(modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]'
    )).filter((element) =>
      !element.disabled &&
      element.tabIndex >= 0 &&
      !element.closest('[hidden], [inert]') &&
      element.getClientRects().length > 0
    );

  const focusInModal = (elements) => {
    const target = getFocusableElements(elements.modal)[0];
    if (target) {
      target.focus();
    }
  };

  // WO-796: every customer word comes from Theme settings > Back in Stock,
  // printed as data-text-* attributes on the modal in layout/theme.liquid.
  const text = (key) => {
    const modal = document.querySelector('[data-mezuzah-bis-modal]');
    return (modal && modal.dataset[key]) || '';
  };

  const getElements = () => {
    return {
      modal: document.querySelector('[data-mezuzah-bis-modal]'),
      form: document.querySelector('[data-mezuzah-bis-form]'),
      email: document.querySelector('[data-mezuzah-bis-email]'),
      variant: document.querySelector('[data-mezuzah-bis-variant]'),
      product: document.querySelector('[data-mezuzah-bis-product]'),
      submit: document.querySelector('[data-mezuzah-bis-submit]'),
      message: document.querySelector('[data-mezuzah-bis-message]')
    };
  };

  const resetFormState = (elements) => {
    if (elements.form) {
      elements.form.reset();
    }

    if (elements.message) {
      elements.message.textContent = '';
      elements.message.removeAttribute('data-status');
    }

    if (elements.submit) {
      elements.submit.disabled = false;
      elements.submit.textContent = text('textSubmit');
    }

    isSubmitting = false;
  };

  const openModal = (trigger) => {
    const elements = getElements();

    if (
      !elements.modal ||
      !elements.form ||
      !elements.email ||
      !elements.variant
    ) {
      return false;
    }

    const variantId = trigger.getAttribute('data-variant-id');
    const productTitle =
      trigger.getAttribute('data-product-title') || text('textFallbackProduct');

    if (!variantId) {
      return false;
    }

    cancelScheduledTransition();
    const generation = ++uiGeneration;
    modalOpen = true;
    lastTrigger = trigger;
    resetFormState(elements);

    elements.variant.value = variantId;

    if (elements.product) {
      elements.product.textContent = productTitle;
    }

    elements.modal.hidden = false;
    isolateBackground(elements.modal);
    document.documentElement.classList.add('mezuzah-bis-open');

    openFrame = window.requestAnimationFrame(() => {
      openFrame = null;
      if (generation !== uiGeneration || !modalOpen) {
        return;
      }
      elements.modal.classList.add('is-open');
      elements.email.focus();
    });
    return true;
  };

  const closeModal = () => {
    const elements = getElements();

    if (!elements.modal || elements.modal.hidden || !modalOpen) {
      return;
    }

    cancelScheduledTransition();
    const generation = ++uiGeneration;
    const trigger = lastTrigger;
    modalOpen = false;
    elements.modal.classList.remove('is-open');
    document.documentElement.classList.remove('mezuzah-bis-open');

    closeTimer = window.setTimeout(() => {
      closeTimer = null;
      if (generation !== uiGeneration || modalOpen) {
        return;
      }
      elements.modal.hidden = true;
      resetFormState(elements);
      restoreBackground();

      if (trigger && trigger.isConnected) {
        trigger.focus();
      }
    }, 200);
  };

  const displayMessage = (elements, text, status) => {
    if (!elements.message) {
      return;
    }

    elements.message.textContent = text;

    if (status) {
      elements.message.setAttribute('data-status', status);
    } else {
      elements.message.removeAttribute('data-status');
    }
  };

  const submitBackInStockRequest = async (form) => {
    if (isSubmitting) {
      return;
    }

    const elements = getElements();
    if (!modalOpen || form !== elements.form) {
      return;
    }
    const generation = uiGeneration;
    const ownsUi = () =>
      generation === uiGeneration &&
      modalOpen &&
      form === getElements().form;
    const email = elements.email
      ? elements.email.value.trim()
      : '';
    const variantId = elements.variant
      ? elements.variant.value.trim()
      : '';

    if (!elements.email || !email || !elements.email.checkValidity()) {
      if (elements.email) {
        elements.email.reportValidity();
      }

      return;
    }

    if (!variantId) {
      displayMessage(
        elements,
        text('textVariantError'),
        'error'
      );
      return;
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email
              }
            }
          }
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + variantId
            }
          }
        }
      }
    };

    isSubmitting = true;

    if (elements.submit) {
      elements.submit.disabled = true;
      elements.submit.textContent = text('textSubmitting');
    }

    displayMessage(elements, '', '');

    try {
      const response = await fetch(KLAVIYO_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
          revision: KLAVIYO_REVISION
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error();
      }

      if (!ownsUi()) {
        return;
      }
      callClarity('event', 'bis_submit_ok');
      form.reset();

      displayMessage(
        elements,
        text('textSuccess'),
        'success'
      );

      if (elements.submit) {
        elements.submit.disabled = true;
        elements.submit.textContent = text('textSuccessButton');
      }
    } catch {
      if (!ownsUi()) {
        return;
      }
      displayMessage(
        elements,
        text('textError'),
        'error'
      );

      if (elements.submit) {
        elements.submit.disabled = false;
        elements.submit.textContent = text('textSubmit');
      }

      isSubmitting = false;
    }
  };

  // WO-796: Broadcast changes the form id even when an option combination
  // has no variant and emits no variantChange. Read the settled form state,
  // including its quantity-rule availability, without changing the buy button.
  const syncPdpButtons = (root = document) => {
    root.querySelectorAll('[data-mezuzah-bis-pdp]').forEach((button) => {
      const form = button.closest('form[data-product-form]');
      const variantId = form?.querySelector('input[name="id"]')?.value.trim() || '';
      const addButton = form?.querySelector('[data-add-to-cart]');
      const holder = button.closest('[data-mezuzah-bis-pdp-holder]');
      button.setAttribute('data-variant-id', variantId);
      if (holder) {
        holder.hidden = !variantId || !addButton || addButton.dataset.mzVariantAvailable === 'true';
      }
    });
  };

  document.addEventListener('change', (event) => {
    if (!event.target || typeof event.target.closest !== 'function') return;
    if (!event.target.matches('input[name="id"]')) {
      return;
    }
    const form = event.target.closest('form[data-product-form]');
    if (form) {
      queueMicrotask(() => syncPdpButtons(form));
    }
  });
  ['theme:product-info:loaded', 'shopify:section:load'].forEach((name) => {
    document.addEventListener(name, (event) => {
      syncPdpButtons(event.target);
      tagLayout(event.target);
    });
  });
  const initialize = () => {
    syncPdpButtons();
    tagLayout();
    if (!layoutTagged) {
      layoutObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => tagLayout(node));
        });
      });
      layoutObserver.observe(document.body, { childList: true, subtree: true });
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  document.addEventListener('click', (event) => {
    if (!event.target || typeof event.target.closest !== 'function') return;
    const openTrigger = event.target.closest(
      '[data-mezuzah-bis-open]'
    );

    if (openTrigger) {
      event.preventDefault();
      if (openModal(openTrigger)) {
        tagLayout(openTrigger);
        callClarity(
          'event',
          openTrigger.hasAttribute('data-mezuzah-bis-pdp')
            ? 'bis_click_pdp'
            : 'bis_click_card'
        );
      }
      return;
    }

    const closeTrigger = event.target.closest(
      '[data-mezuzah-bis-close]'
    );

    if (closeTrigger) {
      event.preventDefault();
      closeModal();
    }
  });

  document.addEventListener('submit', (event) => {
    if (!event.target.matches('[data-mezuzah-bis-form]')) {
      return;
    }

    event.preventDefault();
    submitBackInStockRequest(event.target);
  });

  // Keep this modal's focus ownership local instead of replacing Broadcast's
  // shared drawer/photo-viewer trap.
  document.addEventListener('keydown', (event) => {
    const elements = getElements();
    if (!elements.modal || elements.modal.hidden) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeModal();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }
    const focusable = getFocusableElements(elements.modal);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first) {
      event.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (!focusable.includes(active) || (event.shiftKey && active === first)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
    event.stopPropagation();
  }, true);

  document.addEventListener('focusin', (event) => {
    const elements = getElements();
    if (
      elements.modal &&
      !elements.modal.hidden &&
      !elements.modal.contains(event.target)
    ) {
      event.stopPropagation();
      focusInModal(elements);
    }
  }, true);
})();